import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { FaEye, FaEyeSlash, FaEnvelope, FaLock } from 'react-icons/fa';
import TurnstileWidget from './TurnstileWidget';

const TURNSTILE_ENABLED = process.env.REACT_APP_TURNSTILE_ENABLED === 'true';
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

const formatRetryAfter = (seconds) => {
  if (!seconds) {
    return '';
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes === 0) {
    return `${remainingSeconds} second${remainingSeconds === 1 ? '' : 's'}`;
  }
  if (remainingSeconds === 0) {
    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  }
  return `${minutes} minute${minutes === 1 ? '' : 's'} ${remainingSeconds} second${remainingSeconds === 1 ? '' : 's'}`;
};

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [requiresTurnstile, setRequiresTurnstile] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const { login } = useAuth();

  const resetTurnstile = () => {
    setTurnstileToken('');
    setTurnstileResetKey((value) => value + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (requiresTurnstile && !turnstileToken) {
      setError('Please complete the security verification before signing in.');
      return;
    }

    setLoading(true);

    try {
      await login(email, password, { turnstileToken });
    } catch (err) {
      const challengeRequired = Boolean(err.challenge_required || err.code === 'turnstile_required' || err.code === 'invalid_turnstile');
      if (challengeRequired) {
        setRequiresTurnstile(true);
      }

      const retryAfterMessage = err.retry_after_seconds
        ? ` Please try again in ${formatRetryAfter(err.retry_after_seconds)}.`
        : '';
      const errorMessage = err.message || err.response?.data?.message || err.response?.data?.error || 'Login failed';
      setError(`${errorMessage}${retryAfterMessage}`);

      if (TURNSTILE_ENABLED && challengeRequired) {
        resetTurnstile();
      } else if (TURNSTILE_ENABLED && err.code === 'too_many_attempts') {
        resetTurnstile();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-purple via-purple-600 to-brand-red flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-1/2 -left-1/2 w-96 h-96 bg-white/10 rounded-full animate-pulse"></div>
        <div className="absolute -bottom-1/2 -right-1/2 w-96 h-96 bg-white/5 rounded-full animate-pulse delay-1000"></div>
      </div>

      <div className="max-w-md w-full space-y-8 relative z-10">
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-brand-radial rounded-full mb-4 animate-float shadow-2xl"></div>
          <h2 className="text-4xl font-bold text-white mb-2">
            Welcome Back
          </h2>
          <p className="text-white/80">
            Sign in to your ZapRF account
          </p>
        </div>

        <form className="mt-8 space-y-6 bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-2xl border border-white/20" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-500/20 backdrop-blur-sm border border-red-500/30 text-red-100 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <div className="relative">
                <FaEnvelope className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/60" />
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="w-full pl-10 pr-4 py-3 border border-white/30 rounded-lg bg-white/10 backdrop-blur-sm text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent transition-all duration-200"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <div className="relative">
                <FaLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/60" />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  className="w-full pl-10 pr-12 py-3 border border-white/30 rounded-lg bg-white/10 backdrop-blur-sm text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent transition-all duration-200"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/60 hover:text-white transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>
          </div>

          {requiresTurnstile && (
            <div className="space-y-2">
              <p className="text-sm text-white/80">
                We need one extra verification step before completing sign-in.
              </p>
              <TurnstileWidget
                key={turnstileResetKey}
                enabled={TURNSTILE_ENABLED}
                siteKey={TURNSTILE_SITE_KEY}
                onVerify={(token) => {
                  setError('');
                  setTurnstileToken(token);
                }}
                onExpire={() => setTurnstileToken('')}
                onError={() => {
                  setTurnstileToken('');
                  setError('Security verification failed. Please try again.');
                }}
              />
            </div>
          )}

          <div className="text-right">
            <Link
              to="/forgot-password"
              className="text-sm text-white/70 hover:text-white transition-colors"
            >
              Forgot password?
            </Link>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading || (requiresTurnstile && !turnstileToken)}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-brand-red to-brand-red-dark hover:from-brand-red-dark hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Signing in...
                </div>
              ) : (
                'Sign in'
              )}
            </button>
          </div>

          <div className="text-center">
            <span className="text-white/60">Don't have an account? </span>
            <Link
              to="/request-license"
              className="font-medium text-white hover:text-white/80 transition-colors"
            >
              Request a license
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
